import { apiService } from './apiService';

class InquiryService {
  // Submit a service inquiry
  async submitInquiry(inquiryData) {
    try {
      const payload = {
        inquiry: inquiryData
      };

      const response = await apiService.post('/api/v1/inquiries', payload);

      return {
        success: true,
        inquiry: response,
        message: 'Your project has been submitted successfully!'
      };
    } catch (error) {
      console.error('Submit inquiry error:', error);

      let errorMessage = 'Failed to submit your project. Please try again.';

      if (error.status === 422) {
        // Handle validation errors
        const errors = error.data?.errors || [];
        if (errors.length > 0) {
          errorMessage = errors.join(', ');
        }
      } else if (error.status === 401) {
        errorMessage = 'You must be signed in to submit a project.';
      }

      return {
        success: false,
        message: errorMessage,
        error
      };
    }
  }

  // Update an existing service inquiry (for drafts)
  async updateInquiry(inquiryId, inquiryData) {
    try {
      const payload = {
        inquiry: inquiryData
      };

      const response = await apiService.request(`/api/v1/inquiries/${inquiryId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
        includeAuth: true
      });

      return {
        success: true,
        inquiry: response,
        message: 'Your project has been updated successfully!'
      };
    } catch (error) {
      console.error('Update inquiry error:', error);

      let errorMessage = 'Failed to update your project. Please try again.';

      if (error.status === 422) {
        // Handle validation errors
        const errors = error.data?.errors || [];
        if (errors.length > 0) {
          errorMessage = errors.join(', ');
        }
      } else if (error.status === 401) {
        errorMessage = 'You must be signed in to update a project.';
      }

      return {
        success: false,
        message: errorMessage,
        error
      };
    }
  }

  // Get user's inquiries
  async getUserInquiries() {
    try {
      const response = await apiService.get('/api/v1/inquiries');
      return {
        success: true,
        inquiries: response
      };
    } catch (error) {
      console.error('Get inquiries error:', error);
      return {
        success: false,
        message: 'Failed to load your projects',
        error
      };
    }
  }

  // Get a specific inquiry by ID
  async getInquiry(inquiryId) {
    try {
      const response = await apiService.get(`/api/v1/inquiries/${inquiryId}`);
      return {
        success: true,
        inquiry: response
      };
    } catch (error) {
      console.error('Get inquiry error:', error);
      return {
        success: false,
        message: 'Failed to load project details',
        error
      };
    }
  }

  // Upload attachment and get URL
  async uploadAttachment(file) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiService.request('/api/v1/attachments', {
        method: 'POST',
        body: formData,
        headers: {
          // Don't set Content-Type, let browser set it for FormData
        },
        includeAuth: true
      });

      return {
        success: true,
        attachment: {
          filename: file.name,
          file_url: response.file_url,
          file_type: file.type
        }
      };
    } catch (error) {
      console.error('Upload attachment error:', error);
      return {
        success: false,
        message: `Failed to upload ${file.name}`,
        error
      };
    }
  }

  // Validate inquiry data
  validateInquiryData(data) {
    const errors = [];

    // Required fields
    if (!data.service_id) {
      errors.push('Please select a service');
    }
    if (!data.details || data.details.trim().length < 10) {
      errors.push('Please provide detailed description (at least 10 characters)');
    }
    if (!data.contact_first_name || data.contact_first_name.trim().length === 0) {
      errors.push('First name is required');
    }
    if (!data.contact_last_name || data.contact_last_name.trim().length === 0) {
      errors.push('Last name is required');
    }
    if (!data.contact_email || !this.isValidEmail(data.contact_email)) {
      errors.push('Valid email address is required');
    }
    if (!data.zipcode || data.zipcode.trim().length === 0) {
      errors.push('Zip code is required');
    } else if (!this.isValidZipCode(data.zipcode)) {
      errors.push('Please provide a valid US zip code (e.g., 12345 or 12345-6789)');
    }

    if (!data.street_address) errors.push('Street address is required');
    if (!data.city) errors.push('City is required');
    if (!data.state) errors.push('State is required');
    if (!data.country) errors.push('Country is required');
    if (!data.latitude || !data.longitude) errors.push('Valid location coordinates are required');


    // Optional field validation
    if (data.start_date && !this.isValidDate(data.start_date)) {
      errors.push('Please provide a valid start date');
    }
    if (data.contact_phone && !this.isValidPhone(data.contact_phone)) {
      errors.push('Please provide a valid phone number');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Helper validation methods
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  isValidDate(dateString) {
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date instanceof Date && !isNaN(date) && date >= today;
  }

  isValidPhone(phone) {
    // US phone validation — 10 local digits, optional +1 country code
    if (!phone) return false;
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) return true;
    if (digits.length === 10) return true;
    return false;
  }

  isValidZipCode(zipcode) {
    // US zip code validation
    // Format examples: 12345, 12345-6789
    const zipcodeRegex = /^\d{5}(-\d{4})?$/;
    return zipcodeRegex.test(zipcode.trim());
  }

  // Get budget options
  getBudgetOptions() {
    return [
      { value: 'under_1000', label: 'Under $1,000' },
      { value: 'between_1000_5000', label: '$1,000 - $5,000' },
      { value: 'between_5000_10000', label: '$5,000 - $10,000' },
      { value: 'between_10000_25000', label: '$10,000 - $25,000' },
      { value: 'over_25000', label: 'Over $25,000' },
      { value: 'not_sure', label: 'Not sure' }
    ];
  }

  // Get project stage options
  getProjectStageOptions() {
    return [
      { value: 'ready_to_hire', label: 'Ready to start work immediately' },
      { value: 'planning_and_budgeting', label: 'Still planning and budgeting' },
      { value: 'need_quote_for_insurance', label: 'Need quote for insurance purposes' }
    ];
  }

  // Get property ownership options
  getPropertyOwnershipOptions() {
    return [
      { value: 'own_and_live', label: 'Own and live in the property' },
      { value: 'landlord', label: 'Property owner/landlord' },
      { value: 'rent_with_permission', label: 'Renter with landlord permission' },
      { value: 'looking_to_buy', label: 'Looking to buy the property' }
    ];
  }
}

// Export singleton instance
export const inquiryService = new InquiryService();